





































































import Vue from "vue";
import { RootState } from "@/store";
import { mapGetters, mapState } from "vuex";
import { ApplicationState } from "@/store/modules/ApplicationModule";

// Vue components
import Password from "vue-password-strength-meter";

// Model
import { ClientUser } from "@/model/data/authentication/ClientUser";
import { CreateClientUserReqData } from "@/model/communication/users/CreateClientUserReqData";
import { ApiResponseStatusEnum } from "@/model/communication/ApiResponseStatusEnum";
import { ConfigurationEntry } from "@/model/communication/application/GetServerConfigurationResponse";
import { GetAvailableLanguagesCommand } from "@/model/communication/application/GetAvailableLanguagesCommand";
import { FetchClientProfilesCommand } from "@/model/communication/users/FetchClientProfilesCommand";
import { CreateClientUserCommand } from "@/model/communication/users/CreateClientUserCommand";
import { EditClientUserCommand } from "@/model/communication/users/EditClientUserCommand";
import { ServiceResponseData } from "@/model/communication/ServiceResponseData";

const minScoreKey: string = "minScore";
const minCharactersKey: string = "minCharacters";

const defaultMinScore: number = 3;
const defaultMinCharacters: number = 8;

export default Vue.extend({
    components: { Password },
    data() {
        return {
            profileNameInternalString: "@$",
            loading: false,
            saving: false,
            showPassword: false,
            justSaved: false,
            emailTemplateAlert: false,

            username: "",
            availableProfiles: [],
            selectedProfileName: "",
            email: "",
            password1: "",
            password2: "",
            availableLanguages: [],
            selectedLanguage: "",
            selectedDateFormat: "YYYY-MM-DD",
            selectedTimeFormat: "ampm",

            usernameRules: [(v: string) => v.length > 3 || "Min 4 characters"],

            errorMessage: "",
            currentScore: 0,
            suggestions: null,
            warning: null,

            availableTimeFormat: [
                {
                    text: `AM/PM (${this.$t("options.forExample")} 2:52 PM)`,
                    value: "ampm",
                },
                {
                    text: `24 ${this.$t("options.hours")} (${this.$t("options.forExample")} 14:52)`,
                    value: "24hr",
                },
            ],
            availableDateFormat: [
                {
                    text: `${this.$t("options.day")}-${this.$t("options.month")}-${this.$t("options.year")} (${this.$t("options.forExample")} 31-12-2022)`,
                    value: "DD-MM-YYYY",
                },
                {
                    text: `${this.$t("options.year")}-${this.$t("options.month")}-${this.$t("options.day")} (${this.$t("options.forExample")} 2022-12-31)`,
                    value: "YYYY-MM-DD",
                },
                {
                    text: `${this.$t("options.month")}-${this.$t("options.day")}-${this.$t("options.year")} (${this.$t("options.forExample")} 12-31-2022)`,
                    value: "MM-DD-YYYY",
                },
            ],
        };
    },

    computed: {
        ...mapGetters(["localeFromOptions"]),
        ...mapState({
            passwordPolicy: function (state: RootState): ConfigurationEntry[] {
                return (state.application as ApplicationState).configurationEntries.filter((c) => c.section == "PASSWORD_POLICY");
            },
            currentUser: (state: RootState) => state.authMod.currentUser,
        }),

        canSave() {
            if (!this.usernameRules.every((r) => r(this.username))) return false;
            if (this.username.length <= 0 || this.selectedProfileName.length <= 0) return false;
            if (this.minScoreRequired == undefined) return false;
            if (this.clientUser != null && (this.password1 == null || this.password1.length == 0) && (this.password2 == null || this.password2.length == 0)) return true;
            if (!(this.samePassword && this.currentScore >= this.minScoreRequired && this.password1.length >= this.secureLength)) return false;
            return true;
        },

        minScoreRequired() {
            try {
                let value = Number((this.passwordPolicy.find((c: ConfigurationEntry) => c.key == minScoreKey) as ConfigurationEntry).value);
                return value >= 0 ? value : defaultMinScore;
            } catch (error) {
                console.error("Cannot read ", minScoreKey, " value from server pw policy configuration. Set it to ", defaultMinScore, " as default.");
                return defaultMinScore;
            }
        },

        secureLength() {
            try {
                let value = Number((this.passwordPolicy.find((c: ConfigurationEntry) => c.key == minCharactersKey) as ConfigurationEntry).value);
                return value >= 0 ? value : defaultMinCharacters;
            } catch (error) {
                console.error("Cannot read ", minCharactersKey, " value from server pw policy configuration. Set it to ", defaultMinCharacters, " as default.");
                return defaultMinCharacters;
            }
        },

        samePassword() {
            return this.password1 != null && this.password2 != null && this.password1.length > 0 && this.password2 == this.password1;
        },
    },
    props: {
        clientUser: ClientUser,
    },
    watch: {
        clientUser(newValue) {
            this.reset(newValue);
        },
    },
    methods: {
        async reset(user: ClientUser) {
            this.errorMessage = "";
            this.currentScore = 0;
            this.suggestions = null;
            this.warning = null;
            this.justSaved = false;
            this.emailTemplateAlert = false;

            this.username = "";
            this.email = "";
            this.selectedProfileName = this.availableProfiles.length > 0 ? this.availableProfiles[0].value : [];
            if (this.localeFromOptions != null) this.selectedLanguage = this.localeFromOptions;
            else if (this.availableLanguages.length > 0) this.selectedLanguage = this.availableLanguages[0];
            else this.selectedLanguage = "";
            this.password1 = "";
            this.password2 = "";

            this.selectedDateFormat = "YYYY-MM-DD";
            this.selectedTimeFormat = "ampm";
            if (this.currentUser != null && this.currentUser.userConfig != null) {
                if (this.currentUser.userConfig.dateFormat != null && this.currentUser.userConfig.dateFormat != undefined)
                    this.selectedDateFormat = this.currentUser.userConfig.dateFormat;
                if (this.currentUser.userConfig.timeFormat != null && this.currentUser.userConfig.timeFormat != undefined)
                    this.selectedTimeFormat = this.currentUser.userConfig.timeFormat;
            }

            if (this.clientUser) {
                this.username = this.clientUser.name;
                this.selectedProfileName = this.clientUser.profile.name;
                this.email = this.clientUser.email;

                if (this.clientUser.userConfig != null) {
                    if (this.clientUser.userConfig.language != null && this.clientUser.userConfig.language != undefined && this.clientUser.userConfig.language.trim() != "")
                        this.selectedLanguage = this.clientUser.userConfig.language;
                    if (this.clientUser.userConfig.dateFormat != null && this.clientUser.userConfig.dateFormat != undefined)
                        this.selectedDateFormat = this.clientUser.userConfig.dateFormat;
                    if (this.clientUser.userConfig.timeFormat != null && this.clientUser.userConfig.timeFormat != undefined)
                        this.selectedTimeFormat = this.clientUser.userConfig.timeFormat;
                }
            }
        },

        async getProfiles() {
            try {
                this.reset(null);
                let res = await new FetchClientProfilesCommand().execute();
                if (res.responseStatus == ApiResponseStatusEnum.Success) {
                    this.availableProfiles = res.clientProfiles.map((p) => ({
                        text: `${p.name.includes(this.profileNameInternalString) ? p.name.substring(0, p.name.lastIndexOf(this.profileNameInternalString)) : p.name} (${
                            p.associatedHierarchyList.length
                                ? p.associatedHierarchyList.map((f) => "📁 " + f.name.replace("/", "Root")).join(", ")
                                : this.$t("user_management.noFolder")
                        })`,
                        value: p.name,
                    }));
                }
            } catch (error) {
                console.log("Something went wrong during FetchClientProfilesCommand execution: ", error);
            }
        },

        async getLanguages() {
            try {
                let res = await new GetAvailableLanguagesCommand().execute();
                if (res.responseStatus == ApiResponseStatusEnum.Success) this.availableLanguages = res.availableLanguages;
                else this.availableLanguages = ["en", "it"];
            } catch (error) {
                console.log("Something went wrong during GetAvailableLanguagesCommand execution: ", error);
            }
        },

        onCloseClick() {
            this.$emit("close");
            this.reset(null);
        },

        async onSaveClick() {
            if (this.canSave && !this.saving) {
                try {
                    this.saving = true;

                    const reqData = new CreateClientUserReqData();
                    reqData.username = this.username;
                    reqData.email = this.email;
                    reqData.password = this.password1;
                    reqData.profileName = this.selectedProfileName;
                    reqData.language = this.selectedLanguage;
                    reqData.dateFormat = this.selectedDateFormat;
                    reqData.timeFormat = this.selectedTimeFormat;
                    let res: ServiceResponseData;
                    if (this.clientUser != null) res = await new EditClientUserCommand(reqData).execute();
                    else res = await new CreateClientUserCommand(reqData).execute();

                    if (res.responseStatus == ApiResponseStatusEnum.Success && res.responseString != "GenericError") {
                        if (this.password1 != null && this.password1.length > 0) this.justSaved = true;
                        else this.onCloseClick();
                    } else if (this.clientUser != null) this.errorMessage = this.$t("user_management.error_editing", { 0: this.username });
                    else {
                        this.errorMessage = this.$t("user_management.error_adding", { 0: this.username });
                        this.reset(null);
                    }
                    this.saving = false;
                } catch (error) {
                    console.log("Something went wrong during CreateClientUserCommand/EditClientUserCommand execution: ", error);
                    this.saving = false;
                }
            }
        },

        onScoreChange(score) {
            this.currentScore = score;
        },

        onFeedbackChange({ suggestions, warning }) {
            this.suggestions = suggestions;
            this.warning = warning;
        },

        copyEmailTemplate(lang: string) {
            if (this.availableLanguages.includes(lang)) {
                let toCopy = this.$t("user_management.email_template_en", { 0: this.selectedProfileName, 1: this.username, 2: this.password1, 3: window.location.origin });
                if (this.$te("user_management.email_template_" + lang))
                    toCopy = this.$t("user_management.email_template_" + lang, { 0: this.selectedProfileName, 1: this.username, 2: this.password1, 3: window.location.origin });
                navigator.clipboard.writeText(toCopy);
                this.emailTemplateAlert = true;
                console.log("Copied to the clipboard: ", toCopy);
            } else {
                console.error("Email template not available in the selected language");
            }
        },
    },
    async mounted() {
        await this.getProfiles();
        await this.getLanguages();
        this.reset(this.clientUser);
    },
});
